import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Shield, Users, Layers, DollarSign, Activity, 
  LogOut, Loader2, TrendingUp, Clock, RefreshCw, Gift, Calendar, BarChart3, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { AdminAnalytics } from '../components/AdminAnalytics';
import { TrialReminders } from '../components/TrialReminders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [forums, setForums] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Forum management dialogs
  const [selectedForum, setSelectedForum] = useState(null);
  const [managingForum, setManagingForum] = useState(false);
  const [extendDays, setExtendDays] = useState('15');
  const [customDays, setCustomDays] = useState('');

  const token = localStorage.getItem('admin_token');

  const api = useCallback(() => {
    return axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    try {
      const [statsRes, usersRes, forumsRes, transRes] = await Promise.all([
        api().get('/admin/stats'),
        api().get('/admin/users'),
        api().get('/admin/forums'),
        api().get('/admin/transactions'),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setForums(forumsRes.data);
      setTransactions(transRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [api, navigate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      trial: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      active: 'bg-green-500/10 text-green-600 border-green-500/30',
      free: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
      expired: 'bg-red-500/10 text-red-600 border-red-500/30',
      cancelled: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
      paid: 'bg-green-500/10 text-green-600 border-green-500/30',
      pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
    };
    return <Badge variant="outline" className={colors[status] || ''}>{status}</Badge>;
  };

  const handleSetFreeAccount = async (forumId) => {
    setManagingForum(true);
    try {
      await api().put(`/admin/forums/${forumId}`, {
        subscription_status: 'free'
      });
      toast.success('Forum set as free account');
      fetchData();
    } catch (error) {
      toast.error('Failed to update forum');
    } finally {
      setManagingForum(false);
      setSelectedForum(null);
    }
  };

  const handleExtendTrial = async (forumId) => {
    const days = extendDays === 'custom' ? parseInt(customDays) : parseInt(extendDays);
    if (!days || days <= 0) {
      toast.error('Please enter a valid number of days');
      return;
    }
    
    setManagingForum(true);
    try {
      await api().put(`/admin/forums/${forumId}`, {
        extend_trial_days: days
      });
      toast.success(`Trial extended by ${days} days`);
      fetchData();
    } catch (error) {
      toast.error('Failed to extend trial');
    } finally {
      setManagingForum(false);
      setSelectedForum(null);
      setExtendDays('15');
      setCustomDays('');
    }
  };

  const handleResetToTrial = async (forumId) => {
    setManagingForum(true);
    try {
      await api().put(`/admin/forums/${forumId}`, {
        subscription_status: 'trial',
        extend_trial_days: 45
      });
      toast.success('Forum reset to trial with 45 days');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset forum');
    } finally {
      setManagingForum(false);
      setSelectedForum(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur-lg">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary text-primary-foreground">
              <Shield className="w-5 h-5" />
            </div>
            <span className="font-bold text-xl">Admin Dashboard</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={fetchData} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Layers className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Forums</p>
                  <p className="text-2xl font-bold">{stats?.total_forums || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Subs</p>
                  <p className="text-2xl font-bold">{stats?.subscriptions?.active || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <DollarSign className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Revenue</p>
                  <p className="text-2xl font-bold">${stats?.revenue?.total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription breakdown */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-muted-foreground">Trial</span>
              <Badge variant="secondary">{stats?.subscriptions?.trial || 0}</Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-muted-foreground">Active</span>
              <Badge className="bg-green-500">{stats?.subscriptions?.active || 0}</Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-muted-foreground">Free</span>
              <Badge className="bg-purple-500">{forums.filter(f => f.subscription_status === 'free').length}</Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-muted-foreground">Expired</span>
              <Badge variant="destructive">{stats?.subscriptions?.expired || 0}</Badge>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Activity Log</TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="reminders" className="gap-2">
              <Bell className="h-4 w-4" />
              Reminders
            </TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="forums">Forums</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {stats?.recent_activity?.map((log, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{log.action}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {log.user_email || 'Anonymous'} 
                          {log.forum_id && ` • Forum: ${log.forum_id.slice(0, 8)}...`}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                  ))}
                  {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No activity yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AdminAnalytics api={api} />
          </TabsContent>

          <TabsContent value="reminders" className="mt-6">
            <TrialReminders api={api} />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>All Users ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Timezone</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'chair' ? 'default' : 'secondary'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.country || '-'}</TableCell>
                        <TableCell className="text-sm">{user.home_timezone}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="forums" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>All Forums ({forums.length})</CardTitle>
                <CardDescription>
                  Manage forum subscriptions, set free accounts, and extend trials
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Invite Code</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Trial Ends</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forums.map((forum) => (
                      <TableRow key={forum.id}>
                        <TableCell className="font-medium">{forum.name}</TableCell>
                        <TableCell className="font-mono text-sm">{forum.invite_code}</TableCell>
                        <TableCell>{forum.actual_member_count || forum.member_count}</TableCell>
                        <TableCell>{getStatusBadge(forum.subscription_status)}</TableCell>
                        <TableCell className="text-sm">
                          {forum.trial_ends_at ? formatDate(forum.trial_ends_at) : '-'}
                        </TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => setSelectedForum(forum)}>
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Manage Forum: {forum.name}</DialogTitle>
                                <DialogDescription>
                                  Update subscription status or extend trial period
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="space-y-6 py-4">
                                {/* Current Status */}
                                <div className="p-4 rounded-lg bg-muted">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-muted-foreground">Current Status</span>
                                    {getStatusBadge(forum.subscription_status)}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Members</span>
                                    <span className="font-medium">{forum.actual_member_count || forum.member_count}</span>
                                  </div>
                                </div>

                                {/* Set as Free Account */}
                                <div className="space-y-2">
                                  <Label className="flex items-center gap-2">
                                    <Gift className="h-4 w-4" />
                                    Free Account
                                  </Label>
                                  <p className="text-sm text-muted-foreground">
                                    Grant this forum free access with no charges
                                  </p>
                                  <Button 
                                    onClick={() => handleSetFreeAccount(forum.id)}
                                    disabled={managingForum || forum.subscription_status === 'free'}
                                    className="w-full gap-2"
                                    variant={forum.subscription_status === 'free' ? 'secondary' : 'default'}
                                  >
                                    {managingForum && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {forum.subscription_status === 'free' ? 'Already Free' : 'Set as Free Account'}
                                  </Button>
                                </div>

                                {/* Extend Trial */}
                                <div className="space-y-2">
                                  <Label className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    Extend Trial Period
                                  </Label>
                                  <div className="flex gap-2">
                                    <Select value={extendDays} onValueChange={setExtendDays}>
                                      <SelectTrigger className="flex-1">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="15">15 days</SelectItem>
                                        <SelectItem value="30">30 days</SelectItem>
                                        <SelectItem value="45">45 days</SelectItem>
                                        <SelectItem value="90">90 days</SelectItem>
                                        <SelectItem value="custom">Custom</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    {extendDays === 'custom' && (
                                      <Input 
                                        type="number" 
                                        placeholder="Days"
                                        value={customDays}
                                        onChange={(e) => setCustomDays(e.target.value)}
                                        className="w-24"
                                      />
                                    )}
                                  </div>
                                  <Button 
                                    onClick={() => handleExtendTrial(forum.id)}
                                    disabled={managingForum}
                                    variant="outline"
                                    className="w-full"
                                  >
                                    {managingForum && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    Extend Trial
                                  </Button>
                                </div>

                                {/* Reset to Trial */}
                                {forum.subscription_status !== 'trial' && (
                                  <div className="pt-2 border-t">
                                    <Button 
                                      onClick={() => handleResetToTrial(forum.id)}
                                      disabled={managingForum}
                                      variant="ghost"
                                      className="w-full text-muted-foreground"
                                    >
                                      Reset to 45-day Trial
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Transactions ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Forum ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Session ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((trans) => (
                      <TableRow key={trans.id}>
                        <TableCell className="text-sm">{formatDate(trans.created_at)}</TableCell>
                        <TableCell className="font-mono text-sm">{trans.forum_id?.slice(0, 8)}...</TableCell>
                        <TableCell>${trans.amount?.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(trans.payment_status)}</TableCell>
                        <TableCell className="font-mono text-xs">{trans.session_id?.slice(0, 20)}...</TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No transactions yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
